import React, { useState, useEffect, useRef } from "react";
import refreshLogo from "../img/refreshLogo.svg";
import Expand_down from "../img/Expand_down.svg";
import Close_round from "../img/Close_round.svg";
import Add_round from "../img/Add_round.svg";
import Add_roundWhite from "../img/Add_roundWhite.svg";
import api from "../../api/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const AddNewApplication = ({ setApplicationType }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState("");
  const [category, setCategory] = useState([]); // Full list from API
  const [filteredCategories, setFilteredCategories] = useState([]); // Filtered result
  const [selectedCategory, setSelectedCategory] = useState(""); // Selected ID
  const [search, setSearch] = useState(""); // Search input
  const [activeDropdown, setActiveDropdown] = useState(null); // "project" | "category" | "limit"
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [apiMessage, setApiMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [projectTypes, setProjectTypes] = useState([]);
  const [licenseLimits, setLicenseLimits] = useState([]);
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [selectedLimits, setSelectedLimits] = useState([]);
  const [featuretitle, setFeaturetitle] = useState("");
  const [features, setFeatures] = useState([
    { name: "", file: null, description: "" },
  ]);

  const [appName, setAppName] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [logoFileId, setLogoFileId] = useState(null);
  const [bannerFileId, setBannerFileId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const projectRef = useRef(null);
  const categoryRef = useRef(null);
  const limitRef = useRef(null);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await api.get("/admin-app/api/v1/list-category", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const results = res.data.data.results || [];
      setCategory(results);
      setFilteredCategories(results); // default show all
    } catch (err) {
      console.error("Error fetching categories", err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchDropdownOptions();
  }, []);

  const handleSearch = async (value) => {
    setSearch(value);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await api.get(
        `/admin-app/api/v1/list-category?context=${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setFilteredCategories(res.data.data.results || []);
    } catch (err) {
      console.error("Search failed", err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        projectRef.current?.contains(e.target) ||
        categoryRef.current?.contains(e.target) ||
        limitRef.current?.contains(e.target)
      ) {
        return;
      }
      setActiveDropdown(null); // Close if clicked outside
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchDropdownOptions = async () => {
    try {
      const token = localStorage.getItem("accessToken"); // use your correct key
      const res = await api.get("/admin-app/api/v1/create-project", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = res.data.data;
      setProjectTypes(data.project_type_choices || []);
      setLicenseLimits(data.license_limit_options || []);
    } catch (err) {
      console.error("Failed to fetch project options:", err);
    }
  };

  const uploadFile = async (file) => {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/admin-app/api/v1/upload-file", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.status === "success") {
        return res.data.data.id; // returns image URL
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!selectedProjectType)
      errors.projectType = "Application Type is required";
    if (!selectedCategory) errors.selectedCategory = "Category is required";
    if (!appName.trim()) errors.appName = "Application Name is required";
    if (!description.trim()) errors.description = "Description is required";
    if (!tagline.trim()) errors.tagline = "Tagline is required";
    if (!logoFileId) errors.logoFile = "Application Icon is required";
    if (!bannerFileId) errors.bannerFile = "Banner Image is required";
    if (selectedProjectType === "normal" && selectedLimits.length === 0) {
      errors.selectedLimits =
        "At least one limit must be selected for Normal applications";
    }
    if (!featuretitle) errors.featuretitle = "Feature Heading is required.";

    features.forEach((feature, index) => {
      if (!feature.name?.trim()) {
        errors[`features[${index}].name`] = "Feature Title is required.";
      }
      if (!feature.description?.trim()) {
        errors[`features[${index}].description`] =
          "Long Description is required.";
      }
      if (!feature.fileId) {
        errors[`features[${index}].file`] = "Feature Logo is required";
      }
    });

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    setFormErrors(errors); // update state for showing under inputs if needed

    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorMessage = errors[firstErrorKey];
      setApiMessage(firstErrorMessage);
      setShowErrorModal(true);

      // Scroll to error input if present
      const errorElement = document.querySelector(
        `[data-error="${firstErrorKey}"]`
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      return;
    }
    try {
      // Validate minimum requirement
      if (!appName || !selectedProjectType) {
        setApiMessage("Application name and type are required.");
        setShowErrorModal(true);
        return;
      }

      // Prepare features array
      const preparedFeatures = features.map((feature) => ({
        logo: feature.fileId,
        name: feature.name,
        description: feature.description,
      }));

      const requestBody = {
        name: appName,
        license_limits: selectedLimits,
        image: bannerFileId,
        logo: logoFileId,
        project_type: selectedProjectType,
        category_id: selectedCategory || null,
        description: description,
        tagline: tagline,
        feature_title: featuretitle || "",
        features: preparedFeatures,
      };

      const token = localStorage.getItem("accessToken");
      const res = await api.post(
        "/admin-app/api/v1/create-project",
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.status === "success") {
        setApiMessage("Application created successfully.");
        setShowSuccessModal(true);

        setAppName("");
        setTagline("");
        setDescription("");
        setSelectedProjectType("");
        setSelectedCategory("");
        setSelectedLimits([]);
        setLogoFile(null);
        setBannerFile(null);
        setLogoFileId(null);
        setBannerFileId(null);
        setFeaturetitle("");
        setFeatures([
          { heading: "", file: null, description: "", name: "", fileId: null },
        ]);
        setSearch("");
        setFormErrors({});
        setApplicationType("");
      } else {
        setApiMessage(res.data.message || "Submission failed.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setApiMessage("Something went wrong during submission.");
      setShowErrorModal(true);
    }
  };

  const handleRemoveFeature = (index) => {
    setFeatures((prev) => {
      if (prev.length === 1) {
        // keep one blank row so the form remains usable/validatable
        return [{ name: "", file: null, description: "", fileId: null }];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center font-semibold text-sm bg-[#CEC7FF4D] my-5 py-2 px-4 rounded">
        <span>Application Details</span>
        {/* <img src={refreshLogo} alt="" className="bg-white p-1 rounded-md" /> */}
      </div>

      <div className="w-full flex flex-col gap-2 text-sm">
        <div className="w-full flex gap-5">
          <div className="w-1/2 relative" ref={projectRef}>
            <label className="text-sm blacktext font-medium">
              Application Type<span className="text-red-500">*</span>
            </label>
            <div
              onClick={() => setActiveDropdown("project")}
              className={`pl-3 h-10 pr-4 border-2 border-[#8C8C8C4D] mt-1 bg-transparent rounded-md font-inter flex items-center justify-between cursor-pointer ${
                selectedProjectType === "" ? "text-[#9CA3AF]" : "text-black"
              }`}
              style={{
                backgroundImage: `url(${Expand_down})`,
                backgroundPosition: "98% center",
                backgroundRepeat: "no-repeat",
              }}
            >
              {selectedProjectType || "Select Application Type"}
            </div>

            {activeDropdown === "project" && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-[#8C8C8C4D] rounded-md shadow-sm max-h-56 overflow-y-auto">
                {projectTypes.map((type, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setSelectedProjectType(type);
                      setApplicationType(type);
                      setActiveDropdown(null);
                    }}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  >
                    {type}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="w-1/2 relative" ref={categoryRef}>
            {/* Visible Dropdown Trigger */}
            <label className="text-sm blacktext font-medium">
              Category<span className="text-red-500">*</span>
            </label>

            <div
              onClick={() => {
                setActiveDropdown("category");
                setFilteredCategories(category); // show all when opened
                setSearch("");
              }}
              className={`mt-1 pl-3 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter flex items-center justify-between cursor-pointer ${
                selectedCategory === "" ? "text-[#9CA3AF]" : "text-black"
              }`}
              style={{
                backgroundImage: `url(${Expand_down})`,
                backgroundPosition: "98% center",
                backgroundRepeat: "no-repeat",
              }}
            >
              {category.find((cat) => cat.id === selectedCategory)?.name ||
                "Category"}
            </div>

            {/* Dropdown content */}
            {activeDropdown === "category" && (
              <div
                className="absolute z-10 mt-1 w-full bg-white border border-[#8C8C8C4D] rounded-md shadow-sm max-h-56 overflow-y-auto"
                style={{ maxHeight: "250px", overflowY: "auto" }}
              >
                <input
                  type="text"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:outline-none"
                  placeholder="Search Category..."
                />

                {filteredCategories.length === 0 ? (
                  <div className="p-2 text-sm text-gray-500">
                    No categories found
                  </div>
                ) : (
                  filteredCategories.map((cat) => (
                    <div
                      key={cat.id}
                      onClick={() => {
                        setSelectedCategory(cat.id);
                        setSearch(cat.name); // optional
                        setActiveDropdown(null);
                      }}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    >
                      {cat.name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="w-full flex gap-5">
          {/* Application Name Field */}
          <div className="w-1/2 flex flex-col gap-1">
            <label className="text-sm blacktext font-medium" htmlFor="appName">
              Application Name<span className="text-red-500">*</span>
            </label>
            <input
              id="appName"
              type="text"
              className="pl-3 w-full h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
              placeholder="Application Name"
              value={appName}
              maxLength={40}
              onChange={(e) => setAppName(e.target.value)}
            />
          </div>

          {/* Add Title Field */}
          <div className="w-1/2 flex flex-col gap-1">
            <label className="text-sm blacktext font-medium" htmlFor="tagline">
              Tagline<span className="text-red-500">*</span>
            </label>
            <input
              id="tagline"
              maxLength={100}
              type="text"
              className="pl-3 w-full h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
              placeholder="Tagline"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
            />
          </div>
        </div>

        <div className="w-full flex flex-col gap-1">
          <label
            htmlFor="description"
            className="text-sm blacktext font-medium"
          >
            Description<span className="text-red-500">*</span>
          </label>
          <textarea
            name=""
            id=""
            placeholder="Description"
            value={description}
            maxLength={330}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full pl-3 pt-2 h-16 pr-4  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none "
          ></textarea>
        </div>
        <div className="w-full flex sm:flex-row flex-col sm:gap-5 gap-4">
          <div className="sm:w-1/2 w-full">
            <span className="blacktext text-sm font-medium">
              Application Icon<span className="text-red-500">*</span>
            </span>
            <div className="w-full px-1 flex items-center gap-6 mt-1 h-10 pr-4  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none ">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="logoUpload"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  setLogoFile(file);

                  if (file) {
                    const uploadedId = await uploadFile(file);
                    setLogoFileId(uploadedId);
                  }
                }}
              />

              <label
                htmlFor="logoUpload"
                className="cursor-pointer bg-primary rounded-md px-3 py-1 text-white hover:scale-105 transition-transform duration-300"
                style={{ willChange: "transform" }}
              >
                Choose File
              </label>

              <span className="text-[#9CA3AF]">
                {logoFile ? logoFile.name : "Upload File"}
              </span>
            </div>
          </div>

          <div className="sm:w-1/2 w-full">
            <span className="blacktext text-sm font-medium">
              Banner Image<span className="text-red-500">*</span>
            </span>
            <div className="w-full px-1 mt-1 flex items-center gap-6  h-10 pr-4  border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none ">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="bannerUpload"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  setBannerFile(file);

                  if (file) {
                    const uploadedId = await uploadFile(file);
                    setBannerFileId(uploadedId);
                  }
                }}
              />

              <label
                htmlFor="bannerUpload" // ✅ fix this line
                className="cursor-pointer bg-primary rounded-md px-3 py-1 text-white hover:scale-105 transition-transform duration-300"
                style={{ willChange: "transform" }}
              >
                Choose File
              </label>

              <span className="text-[#9CA3AF]">
                {bannerFile ? bannerFile.name : "Upload File"}
              </span>
            </div>
          </div>
        </div>

        {selectedProjectType.toLowerCase() === "normal" &&(

          <div className="w-full flex gap-5">
          <div className="w-full flex flex-col gap-2">
            <div className="flex justify-between items-center font-semibold text-sm bg-[#CEC7FF4D] my-5 py-2 px-4 rounded">
              <span>App Limits</span>
              {/* <img src={refreshLogo} alt="" className="bg-white p-1 rounded-md" /> */}
            </div>

            <div className="flex gap-4">
              {/* Limit Dropdown with Label */}
              <div className="w-1/2 relative " ref={limitRef}>
                <label className="text-sm blacktext font-medium">
                  Limit Type<span className="text-red-500">*</span>
                </label>

                <div
                  onClick={() => setActiveDropdown("limit")}
                  className={`pl-3 h-10 pr-3 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter flex items-center justify-between cursor-pointer ${
                    selectedLimits === "" ? "text-[#9CA3AF]" : "text-black"
                  }`}
                  style={{
                    backgroundImage: `url(${Expand_down})`,
                    backgroundPosition: "98% center",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <span className="text-[#9CA3AF]">
                    {selectedLimits.length === 0
                      ? "Select Limit Type"
                      : "Select Limit Type"}
                  </span>
                </div>

                {activeDropdown === "limit" && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-[#8C8C8C4D] rounded-md shadow-sm max-h-56 overflow-y-auto">
                    {licenseLimits.map((limit, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          if (selectedLimits.includes(limit)) {
                            setSelectedLimits(
                              selectedLimits.filter((l) => l !== limit)
                            ); // remove
                          } else {
                            setSelectedLimits([...selectedLimits, limit]); // add
                          }
                          setActiveDropdown(null); // close dropdown
                        }}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        {limit}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Placeholder spacing */}
              <span className="w-1/2"></span>
            </div>

            {/* Selected Limit Tags */}
            <div className="flex gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                {selectedLimits.length === 0 ? (
                  <span className="text-[#9CA3AF]">
                    No limits selected yet.
                  </span>
                ) : (
                  selectedLimits.map((limit, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between px-3 gap-4 h-10 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter bg-right bg-no-repeat"
                    >
                      <span>{limit}</span>
                      <img
                        src={Close_round}
                        alt="Remove"
                        onClick={() =>
                          setSelectedLimits(
                            selectedLimits.filter((l) => l !== limit)
                          )
                        }
                        className="cursor-pointer"
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
          
        )}

        
        {/* 
        <div className="w-full flex gap-5">
          <div className="w-full flex flex-col gap-4">
            <span className="text-lg font-semibold">App Features</span>

            <div className="flex gap-4">
              <input
                type="text"
                placeholder="Feature Heading"
                className="w-full relative pl-3 h-10 pr-3 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                value={featuretitle}
                onChange={(e) => setFeaturetitle(e.target.value)}
              />
            </div>
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex flex-row sm:flex-col gap-4 border border-1 p-4 rounded-lg"
              >
                <div className="flex flex-row w-full gap-4 items-center">
                  <input
                    type="text"
                    placeholder="Feature Title"
                    className="pl-3 h-10 pr-3 border-2 border-[#8C8C8C4D] w-1/2 bg-transparent rounded-md font-inter focus:outline-none"
                    value={feature.name}
                    onChange={(e) => {
                      const updated = [...features];
                      updated[index].name = e.target.value;
                      setFeatures(updated);
                    }}
                  />

                  <div className=" px-1 flex flex-grow items-center gap-6 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`featureLogo_${index}`}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        const updated = [...features];
                        updated[index].file = file;

                        if (file) {
                          const uploadedId = await uploadFile(file);
                          updated[index].fileId = uploadedId;
                        }
                        setFeatures(updated);
                      }}
                    />

                    <label
                      htmlFor={`featureLogo_${index}`}
                      className="cursor-pointer bg-primary rounded-md px-3 py-1 text-white"
                    >
                      Choose File
                    </label>

                    <span className="text-[#9CA3AF]">
                      {feature.file ? feature.file.name : "Upload File"}
                    </span>
                  </div>
                  {features.length > 1 && index > 0 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className=" w-8 h-8 
                  text-gray-400 text-sm font-bold"
                      aria-label={`Remove feature ${index + 1}`}
                      title="Remove feature"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <textarea
                  placeholder="Long Description"
                  className="w-full pl-3 pt-2 h-full pr-3 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                  value={feature.description}
                  onChange={(e) => {
                    const updated = [...features];
                    updated[index].description = e.target.value;
                    setFeatures(updated);
                  }}
                />
              </div>
            ))}
          </div>
        </div> */}

        <div className="w-full flex gap-5">
          <div className="w-full flex flex-col gap-2">
            <div className="flex justify-between items-center font-semibold text-sm bg-[#CEC7FF4D] my-5 py-2 px-4 rounded">
              <span>App Features</span>
              {/* <img src={refreshLogo} alt="" className="bg-white p-1 rounded-md" /> */}
            </div>

            {/* Feature Heading Input */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium blacktext">
                Feature Heading<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Feature Heading"
                maxLength={90}
                className="w-full pl-3 h-10 pr-3 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                value={featuretitle}
                onChange={(e) => setFeaturetitle(e.target.value)}
              />
            </div>

            {/* Feature Blocks */}
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex flex-row sm:flex-col gap-2 border border-1 p-4 rounded-lg"
              >
                <div className="flex flex-col sm:flex-row w-full gap-4 items-start sm:items-center">
                  {/* Feature Title */}
                  <div className="w-full sm:w-1/2 flex flex-col gap-1">
                    <label className="text-sm font-medium blacktext">
                      Feature Title<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={30}
                      placeholder="Feature Title"
                      className="pl-3 h-10 pr-3 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                      value={feature.name}
                      onChange={(e) => {
                        const updated = [...features];
                        updated[index].name = e.target.value;
                        setFeatures(updated);
                      }}
                    />
                  </div>

                  {/* Feature Image Upload */}
                  <div className="w-full sm:w-1/2 flex flex-col gap-1">
                    <label className="text-sm font-medium blacktext">
                      Feature Logo<span className="text-red-500">*</span>
                    </label>
                    <div className="flex w-full">
                      <div className="px-1 w-full flex items-center gap-6 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id={`featureLogo_${index}`}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            const updated = [...features];
                            updated[index].file = file;

                            if (file) {
                              const uploadedId = await uploadFile(file);
                              updated[index].fileId = uploadedId;
                            }
                            setFeatures(updated);
                          }}
                        />
                        <label
                          htmlFor={`featureLogo_${index}`}
                          className="cursor-pointer bg-primary rounded-md px-3 py-1 text-white hover:scale-105 transition-transform duration-300"
                          style={{ willChange: "transform" }}
                        >
                          Choose File
                        </label>
                        <span className="text-[#9CA3AF]">
                          {feature.file ? feature.file.name : "Upload File"}
                        </span>
                      </div>
                      {features.length > 1 && index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(index)}
                          className="w-8 h-8 text-gray-400 text-sm font-bold self-center mt-6 sm:mt-0 hover:scale-125 hover:text-red-600 transition-transform duration-300"
                          aria-label={`Remove feature ${index + 1}`}
                          title="Remove feature"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Remove Feature Button */}
                </div>

                {/* Long Description */}
                <div className="w-full flex flex-col gap-1">
                  <label className="text-sm font-medium blacktext">
                    Long Description<span className="text-red-500">*</span>
                  </label>
                  <textarea
                    placeholder="Long Description"
                    maxLength={330}
                    className="w-full pl-3 pt-2 h-full pr-3 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                    value={feature.description}
                    onChange={(e) => {
                      const updated = [...features];
                      updated[index].description = e.target.value;
                      setFeatures(updated);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="group flex justify-between items-center w-40 cursor-pointer rounded-md py-2 bg-[#CEC7FF4D] px-2
          hover:bg-primary hover:text-[white] font-semibold text-primary hover:scale-105 transition-transform duration-300"
          onClick={() =>
            setFeatures((prev) => [
              ...prev,
              { name: "", file: null, fileId: null, description: "" },
            ])
          }
          style={{ willChange: "transform" }}
        >
          <img src={Add_round} alt="" className="block group-hover:hidden" />
          <img
            src={Add_roundWhite}
            alt=""
            className="hidden group-hover:block"
          />
          <span className="">Add New Feature</span>
        </div>

        <div className="flex justify-end">
          <button
            className="bg-primary w-64 py-2 text-white font-semibold rounded-md  hover:scale-105 transition-transform duration-300"
            style={{
              willChange: "transform",
            }}
            onClick={handleSubmit}
          >
            Save
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showSuccessModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                className="w-full max-w-md bg-white rounded-xl shadow-xl"
              >
                <div className="flex justify-center pt-8">
                  <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50">
                    <motion.div
                      className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50"
                      animate={{
                        rotate: [0, -10, 10, -10, 10, 0], // shake motion
                      }}
                      transition={{
                        duration: 0.6,
                        ease: "easeInOut",
                      }}
                    >
                      <svg
                        className="w-12 h-12 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </motion.div>
                  </div>
                </div>
                <div className="px-6 py-4 text-center">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    Success!
                  </h3>
                  <p className="text-gray-600 mb-6">{apiMessage}</p>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      navigate("/myapplication");
                    }}
                    className="w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg"
                  >
                    Continue
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showErrorModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                className="w-full max-w-md bg-white rounded-xl shadow-xl"
              >
                <div className="flex justify-center pt-8">
                  <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50">
                    {/* Close (X) icon instead of checkmark */}
                    <motion.div
                      className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50"
                      animate={{
                        rotate: [0, -10, 10, -10, 10, 0], // shake motion
                      }}
                      transition={{
                        duration: 0.6,
                        ease: "easeInOut",
                      }}
                    >
                      <svg
                        className="w-8 h-8 text-red-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeWidth={2}
                          strokeLinecap="round"
                          d="M12 8v4m0 4h.01M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"
                        />
                      </svg>
                    </motion.div>
                  </div>
                </div>
                <div className="px-6 py-4 text-center">
                  <p className="text-gray-600 mb-6">{apiMessage}</p>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setShowErrorModal(false)}
                    className="w-full py-3 px-4 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg"
                  >
                    Try Again
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Loading overlay */}
    </div>
  );
};

export default AddNewApplication;
